import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { Contracts } from "@/src/gql/graphql";
import type { SavedViewConfig } from "@/lib/types/table";
import {
  AWAITING_SIGNATURE_STATUSES,
  CONTRACT_STATUS_PRESENTATION,
  CONTRACT_STATUS_VALUES,
  type ContractStatusTone,
} from "@/lib/constants/contract-statuses";
import { contractDetailActions } from "./ContractDetailConfig";
import {
  textField,
  selectField,
  dateField,
  numberField,
  fkField,
  STANDARD_TABLE_FEATURES,
} from "@/features/crm-table-config/table-config-builders";

/**
 * A shared semantic tone rendered in this table's colour vocabulary.
 *
 * The tone (and the label beside it) come from `@wtc/contract-signing`, which
 * every contract surface reads — Hub's table, the detail tab, the Slack deal
 * card and the iOS chase screen. Only the mapping from tone to THIS table's
 * colour name lives here, because a colour name is a rendering decision and
 * belongs to the renderer.
 */
const TONE_COLOR: Record<ContractStatusTone, string> = {
  neutral: "gray",
  info: "blue",
  progress: "amber",
  positive: "green",
  negative: "red",
};

/**
 * Contract status options + colors. Contracts are real e-signature documents;
 * status is driven by the e-signature provider lifecycle, not editable inline.
 */
export const CONTRACT_STATUS_OPTIONS = CONTRACT_STATUS_VALUES.map((value) => ({
  label: CONTRACT_STATUS_PRESENTATION[value].label,
  value,
  color: TONE_COLOR[CONTRACT_STATUS_PRESENTATION[value].tone],
}));

/** Renewal lookahead window for the "Renewing Soon" saved view, in days. */
const RENEWAL_LOOKAHEAD_DAYS = 60;

function renewalHorizonDate(): string {
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + RENEWAL_LOOKAHEAD_DAYS);
  return horizon.toISOString().split("T")[0];
}

const contractSavedViews: SavedViewConfig[] = [
  {
    id: "all-contracts",
    name: "All Contracts",
    filters: {},
    isDefault: true,
  },
  {
    id: "sent-by-me",
    name: "Sent by Me",
    // Mirrors the deals "My Deals" placeholder convention (`current_user` resolved
    // to the signed-in user's id by the filter-conversion service).
    filters: { filter_sent_by_user_id: ["current_user"] },
  },
  {
    id: "awaiting-signature",
    name: "Awaiting Signature",
    filters: { filter_status: [...AWAITING_SIGNATURE_STATUSES] },
  },
  {
    id: "signed",
    name: "Signed",
    filters: { filter_status: ["signed"] },
  },
  {
    id: "renewing-soon",
    name: "Renewing Soon",
    // Mirrors the deals "Overdue Deals" static-relative-date pattern
    // (deal-saved-views.ts) — computed at module load, same as that view.
    // Only signed contracts have a real term/expiry worth renewing.
    filters: {
      filter_status: ["signed"],
      filter_expires_at_min: new Date().toISOString().split("T")[0],
      filter_expires_at_max: renewalHorizonDate(),
    },
  },
  {
    id: "expired",
    name: "Expired",
    filters: {
      filter_status: ["signed"],
      filter_expires_at_max: new Date().toISOString().split("T")[0],
    },
  },
  {
    id: "declined-withdrawn",
    name: "Declined / Withdrawn",
    filters: { filter_status: ["declined", "withdrawn"] },
  },
];

export const CONTRACTS_CONFIG: SimpleTableConfig<Contracts> = {
  entity: "contracts",
  displayName: "Contracts",
  statusFieldId: "status",
  defaultPageSize: 25,
  defaultSort: { field: "sent_at", direction: "desc" },
  rowActionsPlacement: "end",
  rowActionsDisplayMode: "hover",

  fields: {
    document_name: textField({
      label: "Document",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      primary: true,
      filterType: "contains",
      width: 240,
      minWidth: 160,
      maxWidth: 400,
      icon: "FileText",
    }),
    template_name: textField({
      label: "Template",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "contains",
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      icon: "LayoutTemplate",
    }),
    status: selectField({
      label: "Status",
      isStatusField: true,
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      badge: true,
      groupable: true,
      filterType: "exact",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      options: CONTRACT_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      icon: "CircleDot",
    }),
    recipient_name: textField({
      label: "Recipient",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "contains",
      width: 180,
      minWidth: 140,
      maxWidth: 280,
      icon: "User",
    }),
    recipient_email: textField({
      label: "Recipient Email",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "contains",
      width: 220,
      minWidth: 160,
      maxWidth: 320,
      icon: "Mail",
    }),
    company_id: fkField("company-select", {
      label: "Company",
      foreignKey: {
        referencedTable: "companies",
        referencedColumn: "id",
        constraintName: "contracts_company_id_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 220,
      minWidth: 160,
      maxWidth: 360,
      icon: "Building2",
    }),
    contact_id: fkField("client-select", {
      label: "Contact",
      foreignKey: {
        referencedTable: "contacts",
        referencedColumn: "id",
        constraintName: "contracts_contact_id_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 220,
      minWidth: 160,
      maxWidth: 360,
      icon: "User",
    }),
    deal_id: fkField("text", {
      label: "Deal",
      foreignKey: {
        referencedTable: "deals",
        referencedColumn: "id",
        constraintName: "contracts_deal_id_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      icon: "Handshake",
    }),
    sent_by_user_id: fkField("user", {
      label: "Sent By",
      foreignKey: {
        referencedTable: "app_users",
        referencedColumn: "id",
        constraintName: "contracts_sent_by_user_id_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 180,
      minWidth: 140,
      maxWidth: 280,
      icon: "UserCheck",
    }),
    delivery_mode: selectField({
      label: "Delivery",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 130,
      minWidth: 100,
      maxWidth: 180,
      options: [
        { label: "Email", value: "email" },
        { label: "Links", value: "links" },
      ],
      icon: "Send",
    }),
    signing_url: textField({
      label: "Signing Link",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      icon: "Link",
    }),
    signed_pdf_url: textField({
      label: "Signed PDF",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      icon: "FileCheck",
    }),
    sent_at: dateField({
      label: "Sent",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "dateRange",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      icon: "Calendar",
    }),
    signed_at: dateField({
      label: "Signed At",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "dateRange",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      icon: "CalendarCheck",
    }),
    term_months: numberField({
      label: "Term (months)",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 130,
      minWidth: 100,
      maxWidth: 180,
      icon: "Hash",
    }),
    expires_at: dateField({
      label: "Expires",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "dateRange",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      icon: "CalendarClock",
    }),
    source: selectField({
      label: "Source",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      badge: true,
      filterType: "exact",
      width: 130,
      minWidth: 100,
      maxWidth: 180,
      options: [
        { label: "eSignatures", value: "esignatures" },
        { label: "Backfill", value: "backfill" },
      ],
      icon: "FileInput",
    }),
  },

  features: { ...STANDARD_TABLE_FEATURES, bulkActions: false },

  actions: {
    create: false,
    edit: true,
    delete: true,
    view: true,
  },

  detailActions: contractDetailActions,
  savedViews: contractSavedViews,
  customFilterHooks: {},
};
